/**
 * Capitalize the first letter of each word in a string
 * @param text - The string to capitalize
 * @returns The string with each word's first letter capitalized
 */
export function capitalizeWords(text: string | undefined | null): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Capitalize only the first letter of a string
 * @param text - The string to capitalize
 * @returns The string with the first letter capitalized
 */
export function capitalizeFirst(text: string | undefined | null): string {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

/**
 * Format a message instant for display in a chat bubble.
 *
 * WHY THIS TAKES TWO SHAPES. The three chat surfaces disagree on how they carry
 * time: `messages.created_at` (and so `Message.timestamp`) is an ISO string,
 * while `DDChatMessage.timestamp` and `InsightsMessage.timestamp` are both
 * typed `number` (epoch ms). Both are real instants, so one helper takes both
 * rather than forcing two call sites to convert.
 *
 * WHY UNPARSEABLE INPUT PASSES THROUGH UNCHANGED. `Message.timestamp` does
 * double duty — an ISO instant from the server, but a PRE-FORMATTED string
 * ("10:30 AM") in the sample-mode fixtures. The local echo of a message you
 * just sent is converted to ISO in this same change; the demo fixtures are
 * deliberately left as they are, since rewriting a dozen demo-only lines buys
 * nothing that this pass-through does not already give. `new Date('10:30 AM')`
 * is Invalid Date, so a naive formatter would turn every correct-looking demo
 * bubble into "Invalid Date". Returning the original string keeps it readable.
 *
 * @param value ISO-8601 string, epoch milliseconds, or a legacy display string
 * @returns A short local time ("14:32"), or the input unchanged if it is not a
 *   parseable instant, or '' when there is nothing to show
 */
export function formatMessageTime(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    // Already-formatted legacy value — show it as-is rather than "Invalid Date".
    return typeof value === 'string' ? value : '';
  }

  return date.toLocaleTimeString(APP_LOCALE, { hour: '2-digit', minute: '2-digit' });
}

/**
 * The ONE locale every displayed date and time is formatted in.
 *
 * The UI is English. Formatting with the device / browser locale (`undefined`
 * or `[]`) put French month names inside English sentences ("Deletion runs on
 * 22 novembre 2026", "sept. 2026 – oct. 2026") — found in the 119-07 UAT. The
 * app's own convention was already 'en-US' (two dozen call sites); this makes
 * it a named constant so new code cannot drift back to the device locale.
 */
export const APP_LOCALE = 'en-US';

/** A date in the app locale. Returns '' for an unparseable value. */
export function formatAppDate(
  value: string | number | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' },
): string {
  if (value === null || value === undefined || value === '') return '';
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(APP_LOCALE, options);
}

/** A time of day in the app locale. Returns '' for an unparseable value. */
export function formatAppTime(value: string | number | Date | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString(APP_LOCALE, { hour: '2-digit', minute: '2-digit' });
}

/**
 * When a conversation last moved, for a list row: "14:32" today,
 * "Yesterday", the weekday within a week, else "Oct 23" (with the year when
 * it is not this year). A value that is not an instant — the sample-mode
 * fixtures carry pre-formatted strings like "2 hours ago" — passes through.
 */
export function formatConversationTime(value: string | null | undefined, now: Date = new Date()): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(now) - day(d)) / 86_400_000);
  if (days <= 0) return formatAppTime(d);
  if (days === 1) return 'Yesterday';
  if (days < 7) return d.toLocaleDateString(APP_LOCALE, { weekday: 'long' });
  return d.toLocaleDateString(
    APP_LOCALE,
    d.getFullYear() === now.getFullYear() ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' },
  );
}
